import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { openReplayStateLogger } from '../../../hooks/open-replay';

export interface CurrencyOption {
  id: string;
  code: string;
  name: string;
  rate: number;
}

interface CurrencyState {
  options: CurrencyOption[];
  selected: CurrencyOption | null;
  isLoading: boolean;
  setOptions: (options: CurrencyOption[]) => void;
  setSelected: (option: CurrencyOption) => void;
  /** Try to auto-select based on detected country code (e.g. "US" -> "USD") */
  autoSelectByCountry: (countryCode: string) => void;
}

/** Map ISO 3166-1 alpha-2 country codes to ISO 4217 currency codes */
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  GB: 'GBP',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  PT: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  LU: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  EE: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  MT: 'EUR',
  CY: 'EUR',
  JP: 'JPY',
  CN: 'CNY',
  IN: 'INR',
  CA: 'CAD',
  AU: 'AUD',
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  NZ: 'NZD',
  KR: 'KRW',
  SG: 'SGD',
  HK: 'HKD',
  BR: 'BRL',
  MX: 'MXN',
  ZA: 'ZAR',
  PL: 'PLN',
  CZ: 'CZK',
  HU: 'HUF',
  IL: 'ILS',
  TW: 'TWD',
  TH: 'THB',
  MY: 'MYR',
  PH: 'PHP',
  ID: 'IDR',
  TR: 'TRY',
  RU: 'RUB',
  AE: 'AED',
  SA: 'SAR',
  CL: 'CLP',
  CO: 'COP',
  AR: 'ARS',
  NG: 'NGN',
  EG: 'EGP',
  RO: 'RON',
  PK: 'PKR',
  BD: 'BDT',
  VN: 'VND',
  KE: 'KES',
};

export const useCurrencyStore = create<CurrencyState>()(
  devtools(
    openReplayStateLogger(
      (set, get) => ({
        options: [],
        selected: null,
        isLoading: true,
        setOptions: (options) => set({ options, isLoading: false }),
        setSelected: (option) => set({ selected: option }),
        autoSelectByCountry: (countryCode) => {
          const { options, selected } = get();
          // Don't override a manual selection
          if (selected) return;

          const currencyCode = COUNTRY_TO_CURRENCY[countryCode] ?? 'USD';
          const match = options.find((o) => o.code === currencyCode);
          if (match) {
            set({ selected: match });
          }
        },
      }),
      'currency'
    ),
    { name: 'Currency' }
  )
);
